import React, { type ReactNode } from "react";
import { DragDropContext, type DropResult } from "react-beautiful-dnd";

import type { CanvasCard, CardType } from "store/types";

interface Props {
  children: ReactNode;
  cards: CanvasCard[];
  onAddCard: (card: CanvasCard, pageIndex: number, insertIndex: number) => void;
  onMoveCard: (cardId: string, fromPage: number, toPage: number, insertIndex: number) => void;
  onReorderCard: (pageIndex: number, fromIndex: number, toIndex: number) => void;
  createDefaultCard: (type: CardType, page?: number) => CanvasCard;
}

const PALETTE_TYPES: CardType[] = [
  "chart",
  "table",
  "summary",
  "kpi",
  "text",
  "ai_card",
  "exposure",
  "cashflow",
  "unfunded",
  "performance",
  "benchmark",
  "dataFreshness",
  "macroBrief"
];

export const DndWrapper: React.FC<Props> = ({
  children,
  cards,
  onAddCard,
  onMoveCard,
  onReorderCard,
  createDefaultCard
}) => {
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const destPageMatch = destination.droppableId.match(/^canvas-page-(\d+)(?:-cont-\d+)?$/);
    if (!destPageMatch) return;
    const destPage = parseInt(destPageMatch[1], 10);

    // Drag from sidebar → create new card
    if (source.droppableId === "sidebar") {
      const typeIndex = source.index;
      const cardType = PALETTE_TYPES[typeIndex];
      if (!cardType) return;
      const isCont = destination.droppableId.includes("-cont-");
      const newCard = createDefaultCard(cardType, destPage);
      if (isCont) {
        // Dropping on a continuation page — append to end of the original page's cards
        const pageCardCount = cards.filter((c) => c.page === destPage).length;
        onAddCard(newCard, destPage, pageCardCount);
      } else {
        onAddCard(newCard, destPage, destination.index);
      }
      return;
    }

    // Drag within canvas
    const sourcePageMatch = source.droppableId.match(/^canvas-page-(\d+)(?:-cont-\d+)?$/);
    if (!sourcePageMatch) return;
    const sourcePage = parseInt(sourcePageMatch[1], 10);

    if (sourcePage === destPage) {
      // Same page reorder
      if (source.index !== destination.index) {
        onReorderCard(sourcePage, source.index, destination.index);
      }
    } else {
      // Cross-page move
      const pageCards = cards.filter((c) => c.page === sourcePage);
      const card = pageCards[source.index];
      if (card) {
        onMoveCard(card.id, sourcePage, destPage, destination.index);
      }
    }
  };

  return <DragDropContext onDragEnd={handleDragEnd}>{children}</DragDropContext>;
};
